<script setup lang="ts">
type StoreCardProps = {
  store: SFCCStore
  storeIndex: number
  isMyStore: boolean
  stock?: StockResult
  productStoreLocation?: ProductStoreLocation
}
const props = defineProps<StoreCardProps>()
const emit = defineEmits<{
  (e: 'select', store: SFCCStore): void
  (e: 'view-details'): void
}>()
const { t } = useI18n()
const detailsId = useId()

const distance = computed(() =>
  props.store.distance === undefined
    ? ''
    : t('product_details_page.find_store.popup.distance_away', {
        distance: props.store.distance,
        unit: props.store.distanceUnit ?? 'km',
      }),
)
</script>

<template>
  <div
    class="store-card"
    :class="{ 'store-card--selected': isMyStore }"
    :data-testid="isMyStore ? 'store-card--selected' : 'store-card'"
  >
    <div class="store-card__heading">
      <div class="store-card__name-wrapper">
        <!-- Stretched over the card so a tap anywhere selects it without nesting the details in a button. -->
        <button
          type="button"
          class="store-card__select"
          data-testid="store-card__select"
          :data-store-id="store.id"
          :aria-pressed="isMyStore"
          :aria-describedby="detailsId"
          @click="emit('select', store)"
        >
          {{ tidyStoreName(store.name) }}
        </button>
        <StoreMyStoreBadge v-if="isMyStore" />
      </div>
      <span v-if="distance" class="store-card__distance" data-testid="store-card__distance">{{ distance }}</span>
    </div>
    <div :id="detailsId" class="store-card__details">
      <StoreStockInformation
        v-if="stock"
        class="store-card__availability"
        :store="store"
        :position="storeIndex"
        :stock="stock"
        :product-store-location="productStoreLocation"
      />
      <p class="store-card__address" data-testid="store-card__address">{{ formatStoreAddress(store) }}</p>
    </div>
    <Button
      v-if="isMyStore"
      variant="text"
      class="store-card__view-details"
      data-testid="store-card__view-details"
      :data-store-id="store.id"
      @click="emit('view-details')"
    >
      {{ $t('set_my_store.view_store_details') }}
    </Button>
  </div>
</template>

<style lang="postcss">
.store-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-30);
  padding: 1.125rem; /* 18px */
  background-color: var(--surface-light-00);
  border: 1px solid var(--border-neutral-20);
  border-radius: var(--border-radius-4);
  box-shadow:
    0 1px 4px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;

  &:hover:not(.store-card--selected) {
    background-color: var(--surface-light-10);
  }

  &:has(.store-card__select:focus-visible) {
    outline: 2px solid var(--border-dark-10);
    outline-offset: 2px;
  }

  .store-card__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-30);
  }

  .store-card__name-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-20);
  }

  .store-card__select {
    color: var(--text-primary);
    font-size: var(--text-base);
    font-weight: bold;
    text-align: left;

    &:focus-visible {
      outline: none;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
    }
  }

  .store-card__distance {
    flex-shrink: 0;
    font-size: var(--text-small);
    color: var(--text-secondary);
  }

  .store-card__details {
    display: flex;
    flex-direction: column;
    gap: var(--space-30);
  }

  /* Availability sits 4px under the name, inside the 8px row gap. */
  .store-card__availability {
    margin-top: calc(-1 * var(--space-20));
    font-size: var(--text-small);
    font-weight: bold;

    .find-in-store-popup__stock-information__additional-message {
      color: var(--text-secondary);
      font-weight: normal;
    }
  }

  .store-card__address {
    margin: 0;
    padding-block: var(--space-20);
    font-size: var(--text-small);
    line-height: 1.4;
    color: var(--text-secondary);
  }

  .store-card__view-details {
    position: relative; /* above the stretched select button */
    align-self: flex-start;
    padding-block: var(--space-20);
    font-size: var(--text-small);
  }

  /* A 1px ring on the 1px border reads as the design's 2px border without shifting the card. */
  &.store-card--selected {
    background-color: var(--surface-light-20);
    border-color: var(--border-dark-10);
    box-shadow: 0 0 0 1px var(--border-dark-10);
    cursor: default;

    .store-card__select {
      cursor: default;
    }

    .store-card__address {
      color: var(--text-primary);
    }
  }
}
</style>
